import { useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../components/controls/button";
import TextInput from "../../components/controls/text-input";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { showSuccess } from "../../components/notification/toastr-actions";
import { useAppSelector } from "../../hooks/useAppSelector";
import { requiredValidator } from "../../helpers/validators";
import { ApplicationSubDepartment } from "../../models/organisation/application-sub-department";
import { closeDrawer } from "../../store/features/drawer/drawer-slice";
import { duplicateValidator } from "../../helpers/validators";
import { MainContent, Footer, ButtonGroup } from "../../styles/shared/drawer";
import {
  updateDepartment,
  setSelectedSubDepartment,
  updateSubDepartmentField,
  UpdateSubDepartmentPayload,
} from "../../store/features/organisation/admin-department-slice";
import Checkbox from "../../components/controls/checkbox";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { SelectChangeEvent } from "@mui/material";
import Dropdown from "../../components/controls/dropdown";
import { enumToOptions } from "../../helpers/enum-helper";
import { ApplicationProductSelectionMode } from "../../models/organisation/application-product-selection-mode";

interface SupplierModalProps {
  id?: number;
}
type DepartmentValidationState = {
  [K in keyof ApplicationSubDepartment]?: string | null;
};

export default function SubDepartmentDrawer({ id }: SupplierModalProps) {
  const dispatch = useAppDispatch();
  const [validation, setValidation] = useState<DepartmentValidationState>()
  const creatingSubDepartment = useAppSelector(
    (state) => state.adminDepartment.creatingSubDepartment
  );
  const selectedDepartment = useAppSelector(
    (state) => state.adminDepartment.selectedDepartment
  );
  const selectedSubDepartment = useAppSelector(
    (state) => state.adminDepartment.selectedSubDepartment
  );

  useEffect(() => {
    if (selectedDepartment && selectedDepartment.subDepartments) {
      const subDepartment = selectedDepartment.subDepartments.find(
        (cat) => cat.id === id
      );
      if (subDepartment) {
        dispatch(setSelectedSubDepartment(subDepartment));
      }
      resetValidation();
    }
  }, [selectedDepartment, id]);


  const onChange = <K extends keyof ApplicationSubDepartment>(
    payload: UpdateSubDepartmentPayload,
    validation?: (input: ApplicationSubDepartment[K]) => string | null
  ) => {

    const { field, value } = payload;
    const errorMessage = validation ? validation(value) : null;

    setValidation((prevState) => ({
      ...prevState,
      [field]: errorMessage,
    }));

    if (!errorMessage) {
      dispatch(updateSubDepartmentField(payload))
    }
  };


  const handleSaveCategories = async () => {
    if (!selectedDepartment || !validateCategoryFields()) return;
    if (creatingSubDepartment) {
      const subDepartmentNameError = duplicateValidator(
        selectedDepartment?.subDepartments ?? [],
        "name",
        selectedSubDepartment?.name || "",
        selectedSubDepartment?.id
      );
      if (subDepartmentNameError) {
        setValidation((prevState) => ({
          ...prevState,
          categoryName: subDepartmentNameError,
        }));
        return;
      }
    }

    const updatedSubDepartments =
      selectedDepartment.subDepartments?.map((subDepartment) =>
        subDepartment.id === selectedSubDepartment?.id
          ? {
            ...subDepartment,
            name: selectedSubDepartment.name || "",
            description: selectedSubDepartment.description || "",
            active: selectedSubDepartment.active || false,
            productSelectionMode: selectedSubDepartment.productSelectionMode
          }
          : subDepartment
      ) || [];

    if (selectedSubDepartment?.id === 0) {
      updatedSubDepartments.push({
        ...selectedSubDepartment,
        name: selectedSubDepartment.name || "",
        description: selectedSubDepartment.description || "",
        active: selectedSubDepartment.active || false,
        productSelectionMode: selectedSubDepartment.productSelectionMode
      });
    }
    const updatedDepartment: ApplicationDepartment = {
      ...selectedDepartment,
      subDepartments: updatedSubDepartments,
    };

    const response = await dispatch(updateDepartment(updatedDepartment));
    if (response) {
      dispatch(closeDrawer());
      dispatch(
        showSuccess(
          creatingSubDepartment
            ? "New category has been successfully added."
            : "Sub departments have been successfully updated."
        )
      );
    }
  };

  const validateCategoryFields = () => {
    const validation = {
      name: requiredValidator(selectedSubDepartment?.name || ""),
      description: requiredValidator(
        selectedSubDepartment?.description || ""
      ),
    };
    setValidation(validation);
    const hasErrors = Object.values(validation).some((error) => error !== null);
    if (hasErrors) {
      return false;
    } else {
      return true;
    }
  };
  const resetValidation = () => {
    setValidation({
      name: null,
      description: null,
    });
  };

  if (selectedSubDepartment == null) {
    return <></>
  }
  return (
    <>
      <MainContent>

        <Container>
          <CheckboxContainer>
            <Checkbox
              label="Active"
              checked={selectedSubDepartment?.active ?? true}
              onChange={(event) =>
                onChange({
                  id: selectedSubDepartment?.id ?? 0,
                  field: "active",
                  value: event.target.checked
                })}

            ></Checkbox>
          </CheckboxContainer>
          <TextInput
            label="Category Name"
            id="categoryNameInput"
            value={selectedSubDepartment?.name || ""}
            error={validation?.name}
            onChange={(event) =>
              onChange({
                id: selectedSubDepartment?.id ?? 0,
                field: "name",
                value: event.target.value
              },
                requiredValidator
              )
            }
          />
          <TextInput
            // multiline={true}
            label="Category Description"
            id="categoryDescriptionInput"
            value={selectedSubDepartment?.description || ""}
            error={validation?.description}
            onChange={(event) =>
              onChange({
                id: selectedSubDepartment?.id ?? 0,
                field: "description",
                value: event.target.value
              },
                requiredValidator
              )
            }
          />
          <Dropdown
            label="Product Selection Mode"
            id="productSelectionModeDropdown"
            value={selectedSubDepartment.productSelectionMode}
            options={enumToOptions(ApplicationProductSelectionMode)}
            variant="outlined"
            onChange={(event: SelectChangeEvent<unknown>) =>
              onChange({
                id: selectedSubDepartment?.id ?? 0,
                field: "productSelectionMode",
                value: event.target.value
              },
                requiredValidator
              )
            }
            autoWidth={false}
            multiple={false}
            native={false}
          />

        </Container>
      </MainContent>
      <Footer>
        <ButtonGroup>
          <Button
            label="Cancel"
            variant="outlined"
            id="btnCancelDrawer"
            onClick={() => dispatch(closeDrawer())}
          />
          <Button
            id="btnCreate"
            label={creatingSubDepartment ? "Create" : "Update"}
            variant="contained"
            onClick={handleSaveCategories}
          />
        </ButtonGroup>
      </Footer>
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const CheckboxContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;